import {
  Bot,
  ChevronDown,
  Database,
  Edit3,
  MessageSquare,
  MessageSquarePlus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { AiUiResponse, TimelineBlock } from "@vouch/shared";
import { Button, Card, Spinner } from "../components/ui";
import { ChatMarkdown } from "../components/ChatMarkdown";
import { confirm } from "../lib/confirm";
import {
  streamAssistant,
  useAiAvailable,
  useClearConversations,
  useConversation,
  useConversations,
  useDeleteConversation,
  useRenameConversation,
  type AssistantStep,
  type ConversationSummary,
} from "../lib/hooks";
import { AI_MODE_OPTIONS, useAiModePreference } from "../lib/preferences";
import { toast } from "../lib/toast";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  steps?: AssistantStep[];
  ui?: AiUiResponse;
}

const SUGGESTIONS = [
  "What's my total balance across all accounts?",
  "How much have I lent out that's still outstanding?",
  "What did I spend the most on this month?",
  "Which loans are due in the next 30 days?",
];

export function AssistantPage(): JSX.Element {
  const { data: available, isLoading } = useAiAvailable();
  const queryClient = useQueryClient();
  const { data: conversations = [] } = useConversations();
  const deleteConversation = useDeleteConversation();
  const renameConversation = useRenameConversation();
  const clearConversations = useClearConversations();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useAiModePreference();
  const [streaming, setStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string>("Ready");
  const [showChatList, setShowChatList] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Tracks which conversation's server history we've already loaded into view,
  // so re-fetches (e.g. after sending) don't clobber the live messages.
  const hydratedRef = useRef<string | null>(null);

  const { data: detail } = useConversation(conversationId);

  useEffect(() => {
    if (detail && hydratedRef.current !== detail.id) {
      hydratedRef.current = detail.id;
      setMessages(
        detail.messages.map((m) => ({
          role: m.role,
          content: m.content,
          steps: m.steps ?? undefined,
        })),
      );
    }
  }, [detail]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamStatus, streaming]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent): void {
      if (chatListRef.current && !chatListRef.current.contains(event.target as Node)) {
        setShowChatList(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (renaming) renameInputRef.current?.focus();
  }, [renaming]);

  useEffect(() => {
    if (available && !renaming) requestAnimationFrame(() => composerRef.current?.focus());
  }, [available, conversationId, renaming]);

  const newChat = (): void => {
    hydratedRef.current = null;
    setConversationId(null);
    setMessages([]);
    setDraft("");
    setShowChatList(false);
    setRenaming(false);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  const switchConversation = (id: string): void => {
    if (id === conversationId) {
      setShowChatList(false);
      return;
    }
    hydratedRef.current = null;
    setConversationId(id);
    setMessages([]);
    setShowChatList(false);
    setRenaming(false);
  };

  const removeConversation = async (id: string, title: string): Promise<void> => {
    const ok = await confirm({
      title: "Delete this conversation?",
      message: title,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    await deleteConversation
      .mutateAsync(id)
      .then(() => {
        toast.success("Conversation deleted");
        if (conversationId === id) {
          const next = conversations.find((c) => c.id !== id);
          if (next) switchConversation(next.id);
          else newChat();
        }
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to delete"));
  };

  const clearAllChats = async (): Promise<void> => {
    if (conversations.length === 0) return;
    const ok = await confirm({
      title: "Clear all chats?",
      message: `This will permanently delete ${conversations.length} chat${
        conversations.length === 1 ? "" : "s"
      } and their messages.`,
      confirmLabel: "Clear all",
      danger: true,
    });
    if (!ok) return;
    await clearConversations
      .mutateAsync()
      .then((result) => {
        toast.success(`Deleted ${result.deletedCount} chat${result.deletedCount === 1 ? "" : "s"}`);
        newChat();
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to clear chats"));
  };

  const startRenaming = (conversation: ConversationSummary): void => {
    setConversationId(conversation.id);
    setRenameValue(conversation.title);
    setRenaming(true);
  };

  const finishRenaming = async (): Promise<void> => {
    if (!conversationId) {
      setRenaming(false);
      return;
    }
    const title = renameValue.trim();
    const currentTitle = conversations.find((c) => c.id === conversationId)?.title ?? "";
    setRenaming(false);
    if (!title || title === currentTitle) return;
    await renameConversation
      .mutateAsync({ id: conversationId, title })
      .then(() => toast.success("Chat renamed"))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to rename chat"));
  };

  async function send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || streaming) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: message }]);
    setDraft("");
    setStreaming(true);
    setStreamStatus("Thinking…");
    setMessages((m) => [...m, { role: "assistant", content: "" }]);

    let streamedText = "";
    try {
      await streamAssistant(
        {
          message,
          history,
          conversationId: conversationId ?? undefined,
          mode,
        },
        (frame) => {
          if (frame.kind === "status") {
            setStreamStatus(frame.label);
          } else if (frame.kind === "tool_start") {
            setStreamStatus(`Running ${prettyTool(frame.tool)}…`);
          } else if (frame.kind === "tool_end") {
            setStreamStatus(frame.ok ? `${prettyTool(frame.tool)} finished` : `${prettyTool(frame.tool)} failed`);
          } else if (frame.kind === "text") {
            streamedText += frame.chunk;
            replaceLastAssistant(setMessages, streamedText);
          } else if (frame.kind === "draft") {
            const draftText = `Action needs confirmation: ${frame.summary}\n\nDraft: ${frame.draftId}`;
            streamedText = streamedText ? `${streamedText}\n\n${draftText}` : draftText;
            replaceLastAssistant(setMessages, streamedText);
          } else if (frame.kind === "done") {
            hydratedRef.current = frame.conversationId;
            setConversationId(frame.conversationId);
            streamedText = frame.response.text || streamedText;
            replaceLastAssistant(setMessages, streamedText, frame.response);
            void queryClient.invalidateQueries({ queryKey: ["ai-conversations"] });
            void queryClient.invalidateQueries({ queryKey: ["ai-conversation"] });
          } else if (frame.kind === "error") {
            streamedText = frame.message;
            replaceLastAssistant(setMessages, streamedText);
          }
        },
      );
    } catch (e) {
      replaceLastAssistant(
        setMessages,
        e instanceof Error ? e.message : "Something went wrong. Please try again.",
      );
    } finally {
      setStreaming(false);
      setStreamStatus("Ready");
      requestAnimationFrame(() => composerRef.current?.focus());
    }
  }

  if (isLoading) return <Spinner />;

  const activeConversation = conversations.find((c) => c.id === conversationId);
  const activeMode = AI_MODE_OPTIONS.find((option) => option.value === mode) ?? {
    value: "read",
    label: "Read only",
    description: "",
  };

  const header = (
    <div className="mb-2 flex min-w-0 items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Bot className="hidden shrink-0 text-brand-500 sm:block" size={22} />
        <h1 className="shrink-0 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"><span className="hidden sm:inline">Finance </span>Assistant</h1>
        <span className="hidden text-slate-300 sm:inline">—</span>
        {activeConversation ? (
          renaming ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => void finishRenaming()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void finishRenaming();
                if (e.key === "Escape") {
                  setRenaming(false);
                  setRenameValue(activeConversation.title);
                }
              }}
              className="min-w-0 flex-1 rounded-lg border border-brand-500 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 outline-none ring-2 ring-brand-500/15"
            />
          ) : (
            <button
              type="button"
              onClick={() => startRenaming(activeConversation)}
              className="group hidden min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-slate-500 hover:bg-white hover:text-brand-500 md:inline-flex"
              title="Edit chat title"
            >
              <span className="truncate">{activeConversation.title}</span>
              <Edit3 size={13} className="shrink-0 opacity-0 transition group-hover:opacity-100" />
            </button>
          )
        ) : (
          <span className="hidden rounded-full bg-white px-3 py-1 text-sm font-medium text-slate-500 ring-1 ring-slate-200 sm:inline">
            New chat
          </span>
        )}
      </div>
      {available && (
        <div className="flex shrink-0 items-center gap-1.5">
          <div className="relative" ref={chatListRef}>
            <button
              type="button"
              onClick={() => setShowChatList((open) => !open)}
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              title="Chat history"
            >
              <MessageSquare size={16} />
              <ChevronDown size={13} className={`absolute translate-x-2.5 translate-y-2.5 transition ${showChatList ? "rotate-180" : ""}`} />
            </button>

            {showChatList && (
              <div className="absolute right-0 top-full z-30 mt-2 w-[calc(100vw-2rem)] max-w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5">
                <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-brand-500">Chat history</p>
                    <p className="text-xs text-slate-400">
                      {conversations.length} saved chat{conversations.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={newChat}
                    className="inline-flex items-center gap-1 rounded-lg bg-accent-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-accent-600"
                  >
                    <MessageSquarePlus size={13} /> New
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto p-2">
                  {conversations.length === 0 ? (
                    <div className="rounded-xl bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
                      No chats yet.
                    </div>
                  ) : (
                    conversations.map((chat) => (
                      <div
                        key={chat.id}
                        className={`group flex items-center gap-2 rounded-xl px-2 py-2 transition ${
                          chat.id === conversationId ? "bg-brand-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => switchConversation(chat.id)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <MessageSquare
                            size={16}
                            className={chat.id === conversationId ? "text-brand-500" : "text-slate-400"}
                          />
                          <span className="min-w-0">
                            <span
                              className={`block truncate text-sm ${
                                chat.id === conversationId
                                  ? "font-semibold text-brand-500"
                                  : "font-medium text-slate-600"
                              }`}
                            >
                              {chat.title}
                            </span>
                            <span className="block truncate text-xs text-slate-400">
                              {formatChatTime(chat.updatedAt)}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void removeConversation(chat.id, chat.title);
                          }}
                          className="rounded-lg p-1.5 text-slate-300 opacity-100 transition hover:bg-red-50 hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100"
                          title="Delete chat"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {conversations.length > 0 && (
                  <div className="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      onClick={() => void clearAllChats()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100"
                    >
                      <Trash2 size={15} /> Clear all chats
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <select
            aria-label="Assistant access"
            className="h-9 max-w-28 rounded-xl border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 shadow-sm outline-none transition hover:border-brand-500/30 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 sm:max-w-none sm:px-3 sm:text-sm"
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
            title="Assistant permission mode"
          >
            {AI_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <Button variant="secondary" onClick={newChat} className="h-9 rounded-xl px-2.5 shadow-sm sm:px-3">
            <MessageSquarePlus size={16} /> <span className="hidden sm:inline">New chat</span>
          </Button>
        </div>
      )}
    </div>
  );

  if (!available) {
    return (
      <div className="mx-auto max-w-5xl">
        {header}
        <Card>
          <p className="text-sm text-slate-500">
            The assistant isn't configured for this environment yet.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full min-h-[calc(100dvh-7.5rem)] w-full max-w-6xl flex-col lg:min-h-[calc(100dvh-3rem)]">
      {header}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 shadow-sm">
        <div className="min-w-0 flex-1 space-y-5 overflow-x-hidden overflow-y-auto p-4 sm:p-6">
          {messages.length === 0 && (
            <div className="flex flex-col items-start">
              <div className="max-w-[92%] rounded-2xl bg-white p-4 text-sm leading-6 text-slate-700 shadow-sm ring-1 ring-slate-200/80 sm:max-w-[75%]">
                <div className="mb-2 flex items-center gap-2 font-semibold text-brand-500">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50"><Bot size={16} /></span>
                  Vouch Finance Assistant
                </div>
                <p>{assistantIntro(mode)}</p>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} />
          ))}

          {streaming && (
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700" aria-live="polite">
              <Sparkles size={14} className="animate-pulse" /> {streamStatus}
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-200 bg-white">
          {messages.length === 0 && (
            <div className="flex gap-2 overflow-x-auto px-3 pb-1 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:px-4">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => {
                    setDraft(suggestion);
                    requestAnimationFrame(() => composerRef.current?.focus());
                  }}
                  className="whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          <form
            className="flex items-end gap-2 p-3 sm:p-4"
            onSubmit={(e) => {
              e.preventDefault();
              void send(draft);
            }}
          >
            <div className="min-w-0 flex-1 rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 shadow-sm transition focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/10">
              <textarea
                ref={composerRef}
                autoFocus
                rows={1}
                aria-label="Ask about your finances"
                className="max-h-36 min-h-8 w-full resize-none bg-transparent py-1 text-sm leading-6 text-slate-800 outline-none placeholder:text-slate-400"
                placeholder="Ask about your finances…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.form?.requestSubmit();
                  }
                }}
                disabled={streaming}
              />
              <div className="flex items-center justify-between gap-2 text-[11px] text-slate-400">
                <span>{activeMode.label}</span>
                <span className="hidden sm:inline">Enter to send · Shift+Enter for a new line</span>
              </div>
            </div>
            <Button className="h-12 w-12 shrink-0 rounded-xl p-0" type="submit" disabled={streaming || !draft.trim()} aria-label="Send message">
              <Send size={18} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function assistantIntro(mode: "read" | "read_add" | "full"): string {
  if (mode === "full") {
    return "Ask me anything about your money. Full control is enabled, so I can read your ledger and help create, edit, or delete entries through Vouch's approval flow.";
  }
  if (mode === "read_add") {
    return "Ask me anything about your money, or tell me to add income, expenses, transfers, and loans. I can create new entries when your request is clear.";
  }
  return "Ask me anything about your money — balances, spending, loans, committees. I read your Vouch data to answer, but I never make changes.";
}

function replaceLastAssistant(
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  content: string,
  ui?: AiUiResponse,
): void {
  setMessages((current) => {
    const next = [...current];
    const last = next.at(-1);
    if (last?.role === "assistant") {
      next[next.length - 1] = { ...last, content, ...(ui ? { ui } : {}) };
    } else {
      next.push({ role: "assistant", content, ...(ui ? { ui } : {}) });
    }
    return next;
  });
}

function prettyTool(tool: string): string {
  return tool.replace(/^draft_/, "draft ").replace(/_/g, " ");
}

function formatChatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function MessageBubble({ message }: { message: ChatMessage }): JSX.Element {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-brand-500 px-4 py-2 text-sm text-white">
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-2">
        <div className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200">
          <ChatMarkdown content={message.content} />
        </div>
        {message.ui && message.ui.blocks.length > 0 && <ResponseBlocks blocks={message.ui.blocks} />}
        {message.ui && message.ui.provenance.toolsUsed.length > 0 && <p className="px-1 text-[11px] text-slate-400">Based on {message.ui.provenance.toolsUsed.map(prettyTool).join(", ")}</p>}
        {message.steps && message.steps.length > 0 && <StepDetails steps={message.steps} />}
      </div>
    </div>
  );
}

function ResponseBlocks({ blocks }: { blocks: TimelineBlock[] }): JSX.Element {
  return <div className="grid gap-2 sm:grid-cols-2">{blocks.map((block, index) => <ResponseBlock key={`${block.kind}-${index}`} block={block} />)}</div>;
}

function ResponseBlock({ block }: { block: TimelineBlock }): JSX.Element | null {
  if (block.kind === "summary") return <div className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{block.label}</p><p className="mt-1 font-bold text-slate-800">{block.value}</p></div>;
  if (block.kind === "table") return <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-3 sm:col-span-2">{block.title && <p className="mb-2 font-semibold text-slate-800">{block.title}</p>}<table className="w-full text-left text-xs"><thead><tr className="border-b border-slate-100">{block.columns.map((column) => <th key={column.key} className="px-2 py-2 font-semibold text-slate-500">{column.label}</th>)}</tr></thead><tbody>{block.rows.map((row, index) => <tr key={index} className="border-b border-slate-50 last:border-0">{block.columns.map((column) => <td key={column.key} className="px-2 py-2 text-slate-700">{String(row[column.key] ?? "—")}</td>)}</tr>)}</tbody></table></div>;
  if (block.kind === "chart") return <div className="rounded-xl border border-slate-200 bg-white p-3 sm:col-span-2"><p className="font-semibold text-slate-800">{block.title}</p><div className="mt-3 space-y-3">{block.series.map((series) => <div key={series.name}><p className="mb-1 text-xs font-medium text-slate-500">{series.name}</p><div className="flex h-24 items-end gap-1">{series.points.map((point) => { const max = Math.max(...series.points.map((item) => Math.abs(item.y)), 1); return <div key={point.x} className="group flex min-w-0 flex-1 flex-col items-center justify-end gap-1"><span className="w-full rounded-t bg-blue-500/80" style={{ height: `${Math.max(4, Math.abs(point.y) / max * 76)}px` }} title={`${point.x}: ${point.y}`} /><span className="max-w-full truncate text-[9px] text-slate-400">{point.x}</span></div>; })}</div></div>)}</div></div>;
  if (block.kind === "image") return <figure className="overflow-hidden rounded-xl border border-slate-200 bg-white sm:col-span-2"><img src={block.thumbnailUrl} alt={block.caption ?? "Assistant attachment"} className="max-h-80 w-full object-contain" />{block.caption && <figcaption className="px-3 py-2 text-xs text-slate-500">{block.caption}</figcaption>}</figure>;
  return null;
}

function StepDetails({ steps }: { steps: AssistantStep[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const failed = steps.filter((s) => !s.ok).length;
  return (
    <div className="px-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
      >
        <Database size={12} /> Ran {steps.length} quer{steps.length === 1 ? "y" : "ies"}
        {failed > 0 ? ` (${failed} failed)` : ""} · {open ? "hide" : "show"}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {steps.map((s, i) => (
            <pre
              key={i}
              className={`overflow-x-auto rounded-lg p-2 text-[11px] ${
                s.ok ? "bg-slate-50 text-slate-500" : "bg-red-50 text-red-600"
              }`}
            >
              {s.sql}
              {s.ok ? `\n-- ${s.rowCount} row(s)` : `\n-- error: ${s.error ?? "failed"}`}
            </pre>
          ))}
        </div>
      )}
    </div>
  );
}
